import React, { createContext, useContext, ReactNode } from 'react';

import { useTeamById } from '../../../hooks/useTeam';
import type { Team } from '../../../services/teams';

interface TeamContextType {
  team: Team | null;
  isLoading: boolean;
  error: Error | null;
}

const TeamContext = createContext<TeamContextType | undefined>(undefined);

interface TeamProviderProps {
  children: ReactNode;
  teamId: string;
}

export function TeamProvider({
  children,
  teamId,
}: TeamProviderProps): JSX.Element {
  const { data: team, isLoading, error } = useTeamById(teamId);

  const value = React.useMemo(
    () => ({
      team: team ?? null,
      isLoading,
      error,
    }),
    [team, isLoading, error],
  );

  return <TeamContext.Provider value={value}>{children}</TeamContext.Provider>;
}

export function useTeam(): TeamContextType {
  const context = useContext(TeamContext);
  if (context === undefined) {
    throw new Error('useTeam must be used within a TeamProvider');
  }
  return context;
}
